import { Component, Inject, OnDestroy } from '@angular/core'
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ComponentStore } from '@ngrx/component-store';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { ColumnFilter, isEqual, keySet, valueKey } from '../core/filter';

/** A checkbox value passes when it's within the (optionally open-ended) numeric range. */
function withinRange(value: any, type: string, range: { min: number | null, max: number | null }): boolean {
  if (type != 'number') return true
  if (range.min == null && range.max == null) return true
  // Non-numeric / N/A values can't satisfy an active numeric range.
  if (typeof value != 'number') return false
  if (range.min != null && value < range.min) return false
  if (range.max != null && value > range.max) return false
  return true
}

export interface ComparableFilterDialogInput {
  /**
   * Resolved inside the dialog rather than before it. Working out the value lists is a
   * server round trip over every comparable in the tenant, and waiting for it before
   * calling dialog.open() left the Filter button doing nothing at all for that long.
   */
  columns: Observable<ColumnFilter[]>
}

export interface ComparableFilterDialogOutput {
  filters: ColumnFilter[]
}

interface State {
  columns: ColumnFilter[],
  // Per-column search of the value list — a dialog-only convenience, not applied to results.
  searches: { [key: string]: string },
  // Which accordion items are open. Mirrors the cdkAccordionItem state so the value
  // lists of closed columns need never be built.
  expanded: { [key: string]: boolean }
}

/** Row height of a value checkbox, in px — the virtual scroller needs it up front. */
const ITEM_SIZE = 32

/** Above this many values a column scrolls virtually instead of rendering in full. */
const VIRTUAL_SCROLL_FROM = 12

@Component({
  selector: 'app-comparable-filter-dialog',
  templateUrl: './comparable-filter-dialog.component.html',
  styleUrls: ['./comparable-filter-dialog.component.scss'],
  providers: [ComponentStore]
})
export class ComparableFilterDialogComponent implements OnDestroy {
  itemSize = ITEM_SIZE
  virtualScrollFrom = VIRTUAL_SCROLL_FROM

  private _columns$ = this.componentStore.select(state => state.columns)
  private _searches$ = this.componentStore.select(state => state.searches)
  private _expanded$ = this.componentStore.select(state => state.expanded)
  columns$ = this.componentStore.select(
    this._columns$,
    this._searches$,
    this._expanded$,
    (columns, searches, expanded) => {
      return columns.map(column => {
        const range = column.range ?? { min: null, max: null }
        const header = {
          ...column,
          search: searches[column.key] ?? '',
          range,
          expanded: expanded[column.key] == true,
          values: [] as { checked: boolean, value: any }[]
        }
        // A column can hold thousands of distinct values, and this projection re-runs on
        // every keystroke and every tick. Closed columns are not on screen, so opening
        // the dialog only pays for the headers.
        if (!header.expanded) {
          return header
        }

        const search = (searches[column.key] ?? '').trim().toLowerCase()
        const ticked = keySet(column.selectedValues)
        const values = [
          {
            checked: column.values.length == column.selectedValues.length,
            value: 'Select All'
          },
          ...column.values.map(value => {
            return {
              checked: ticked.has(valueKey(value)),
              value: value != null ? value : 'N/A'
            }
          })
        ]
        return {
          ...header,
          // Keep "Select All" pinned; the rest is narrowed by the per-column search
          // and, for numbers, the From/To range.
          values: values.filter(item => {
            if (item.value == 'Select All') return true
            if (search && !String(item.value).toLowerCase().includes(search)) return false
            return withinRange(item.value, column.type, range)
          })
        }
      })
    }
  )

  private _onDestroy = new Subject<void>()
  private _loading$ = new BehaviorSubject<boolean>(true)
  loading$ = this._loading$.asObservable()

  constructor(
    public dialogRef: MatDialogRef<ComparableFilterDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ComparableFilterDialogInput,
    private componentStore: ComponentStore<State>
  ) {
    data.columns.pipe(take(1), takeUntil(this._onDestroy)).subscribe({
      next: columns => {
        this.componentStore.setState({
          columns,
          searches: {},
          expanded: {}
        })
        this._loading$.next(false)
      },
      // Nothing to filter with — close rather than leave the spinner turning.
      error: () => this.dialogRef.close()
    })
  }

  ngOnDestroy() {
    this._onDestroy.next()
    this._onDestroy.complete()
  }

  trackBy(index: number, item: {key: string}) {
    return item.key
  }

  /**
   * Height of the value list. The virtual scroller needs a definite height, and a
   * constant one at that — it caches the viewport size — so the short lists that fit
   * render in full instead.
   */
  listHeight(count: number): number {
    return Math.min(count, VIRTUAL_SCROLL_FROM) * ITEM_SIZE
  }

  onExpandedChange(key: string, expanded: boolean) {
    this.componentStore.patchState(state => ({
      ...state,
      expanded: { ...state.expanded, [key]: expanded }
    }))
  }

  trackByIndex(index: number, item: any) {
    return index
  }

  onClose() {
    this.dialogRef.close()
  }

  onSubmit() {
    this.submit()
  }

  private submit = this.componentStore.effect<void>(trigger$ =>
    trigger$.pipe(
      withLatestFrom(this.componentStore.state$),
      tap(([_, state]) => {
        this.dialogRef.close({filters: state.columns})
      })
    )
  )

  onChangeSearch(key: string, search: string) {
    this.componentStore.patchState(state => ({
      ...state,
      searches: { ...state.searches, [key]: search }
    }))
  }

  onChangeRange(key: string, bound: 'min' | 'max', raw: string) {
    const parsed = raw == null || raw.trim() == '' ? null : Number(raw)
    const value = parsed != null && !isNaN(parsed) ? parsed : null
    this.patchColumn(key, column => ({
      ...column,
      range: { ...(column.range ?? { min: null, max: null }), [bound]: value }
    }))
  }

  onChangeMonth(key: string, raw: string) {
    const parsed = raw == null || raw.trim() == '' ? null : Number(raw)
    const month = parsed != null && parsed >= 1 && parsed <= 12 ? parsed : null
    this.patchColumn(key, column => ({ ...column, month }))
  }

  onChangeYear(key: string, raw: string) {
    const parsed = raw == null || raw.trim() == '' ? null : Number(raw)
    const year = parsed != null && !isNaN(parsed) ? parsed : null
    this.patchColumn(key, column => ({ ...column, year }))
  }

  private patchColumn(key: string, update: (column: ColumnFilter) => ColumnFilter) {
    this.componentStore.patchState(state => ({
      ...state,
      columns: state.columns.map(column => column.key == key ? update(column) : column)
    }))
  }

  onChangeValue(key: string, value: any, event: MatCheckboxChange) {
    this.componentStore.patchState((state) => {
      const columns = state.columns.map(column => {
        if (column.key != key) return column;

        if (value == 'Select All') {
          return event.checked
            ? {...column, selectedValues: [...column.values]}
            : {...column, selectedValues: []}
        }

        if (value == 'N/A') {
          return event.checked
            ? {...column, selectedValues: [...column.selectedValues, null]}
            : {...column, selectedValues: column.selectedValues.filter(v => !isEqual(v, null))}
        }

        return event.checked
          ? {...column, selectedValues: [...column.selectedValues, value]}
          : {...column, selectedValues: column.selectedValues.filter(v => !isEqual(v, value))}
      })
      return {
        ...state,
        columns
      }
    })
  }
}
